import React, { useState } from 'react';
import { Alert, Modal, ModalBody, ModalCloseButton, ModalFooterButtons, ModalHeader } from '@televet/kibble-ui';
import {
  LoyaltyReward,
  LoyaltyRewardRedemptionStatus,
  useClaimRewardMutation,
  useFulfillRewardMutation,
  useSubscribeToLoyaltyRewardRedemptionUpdatedSubscription,
} from 'shared/types/graphql';
import { RewardCard } from './RewardCard';
import { STATUSES, useCustomToast } from 'shared/hooks/useCustomToast';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { LoyaltyEvents } from 'shared/providers/TrackingProvider/EventNames';
import { useTracking } from 'shared/providers/TrackingProvider';

interface RewardActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  reward: Pick<LoyaltyReward, 'title' | 'id' | 'description' | 'pointCost'>;
  rewardRedemptionId?: string;
  requestedDate?: Date;
  accountId: string;
  triggerRefetch?: () => void;
}

export const RewardActionModal = ({
  isOpen,
  onClose,
  reward,
  rewardRedemptionId,
  requestedDate,
  accountId,
  triggerRefetch,
}: RewardActionModalProps): JSX.Element => {
  const { toastMessagePop } = useCustomToast();
  const { primaryIntegrationName } = useIntegrationsProvider();
  const { trackEvent } = useTracking();
  const [currentRewardRedemptionId, setCurrentRewardRedemptionId] = useState(rewardRedemptionId);
  const [isWaitingForUpdate, setIsWaitingForUpdate] = useState(false);

  const [fulfillReward, { loading: isRewardBeingFulfilled }] = useFulfillRewardMutation();
  const [claimReward, { loading: isRewardBeingClaimed }] = useClaimRewardMutation();

  const handleFulfill = async (): Promise<void> => {
    setIsWaitingForUpdate(true);
    try {
      await fulfillReward({
        variables: {
          data: {
            loyaltyRewardRedemptionId: rewardRedemptionId,
          },
        },
      });
    } catch {
      setIsWaitingForUpdate(false);
      toastMessagePop(
        `Whoops! There was a problem fulfilling this reward, please refresh the page and try again.`,
        STATUSES.error,
      );
    }
  };

  const handleClaim = async (): Promise<void> => {
    setIsWaitingForUpdate(true);
    try {
      const response = await claimReward({
        variables: {
          data: {
            loyaltyRewardId: reward.id,
            loyaltyAccountId: accountId,
          },
        },
      });

      if (response.data?.requestLoyaltyReward) {
        trackEvent(LoyaltyEvents.SpendVirtualCurrency, { reward: reward.title, pointsSpent: reward.pointCost });
        setCurrentRewardRedemptionId(response.data?.requestLoyaltyReward.id);
      }
    } catch {
      setIsWaitingForUpdate(false);
      toastMessagePop(
        `Whoops! There was a problem claiming this reward, please refresh the page and try again.`,
        STATUSES.error,
      );
    }
  };

  useSubscribeToLoyaltyRewardRedemptionUpdatedSubscription({
    variables: {
      where: {
        id: currentRewardRedemptionId,
      },
    },
    skip: !currentRewardRedemptionId,
    onData: ({ data }) => {
      if (data.data?.loyaltyRewardRedemptionUpdated.mutation === 'update') {
        const status = data.data.loyaltyRewardRedemptionUpdated.node.status;
        toastMessagePop(
          `Reward ${status === LoyaltyRewardRedemptionStatus.PendingFulfillment ? 'Claimed' : 'Fulfilled'}!`,
        );
        if (triggerRefetch) {
          triggerRefetch();
        }
      }
      setIsWaitingForUpdate(false);
      onClose();
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={(): void => {
        trackEvent(requestedDate ? LoyaltyEvents.FulfillModalDismiss : LoyaltyEvents.ClaimModalDismiss);
        onClose();
      }}
      isCentered
      size="sm"
    >
      <ModalCloseButton />
      <ModalHeader>{requestedDate ? 'Fulfill' : 'Claim'} Reward</ModalHeader>
      <ModalBody pt={4} pb={2}>
        <RewardCard reward={reward} requestedDate={requestedDate} showButton={false} accountId={accountId} />
        {requestedDate && (
          <Alert
            title="Important Instructions"
            description={`To fulfill this reward go to the invoice in ${primaryIntegrationName} where this line item resides and discount
              that amount.`}
            status="warning"
            hideCloseButton
            mt={6}
            iconProps={{ name: 'infoCircle' }}
          />
        )}
      </ModalBody>
      <ModalFooterButtons
        onSubmit={(): void => {
          trackEvent(requestedDate ? LoyaltyEvents.FulfillModalConfirm : LoyaltyEvents.ClaimModalConfirm);
          requestedDate ? handleFulfill() : handleClaim();
        }}
        submitText="Confirm"
        isLoading={isRewardBeingFulfilled || isRewardBeingClaimed || isWaitingForUpdate}
        isDisabled={isRewardBeingFulfilled || isRewardBeingClaimed || isWaitingForUpdate}
      />
    </Modal>
  );
};
